import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  ProgressComparisonEntry,
  ProgressMetricSeries,
  ProgressSeriesPoint,
} from '@/api/progress/progressApi.types';
import { Button } from '@/components/ui/button';
import { MetricLineChart, type MetricLinePoint } from '@/components/common/charts/MetricLineChart';
import { formatIsoDateToDayAndMonth } from '@/utils/dateFormatting';
import { BeforeAfterComparison } from './BeforeAfterComparison';
import { formatProgressNumber } from './progressFormatting';

interface ProgressOverviewProps {
  series: ProgressMetricSeries[];
  comparisonEntries: ProgressComparisonEntry[];
}

function toChartPoint(point: ProgressSeriesPoint): MetricLinePoint {
  return { date: point.recordedOn, value: point.value, detail: point.ownerFeedback };
}

export function ProgressOverview({ series, comparisonEntries }: ProgressOverviewProps) {
  const { t } = useTranslation('progress');
  const [selectedMetricCode, setSelectedMetricCode] = useState<string | null>(null);
  const firstSeriesEntry = series[0];
  const hasSelectedMetric =
    selectedMetricCode !== null &&
    series.some(function matchesSelected(entry) {
      return entry.metricCode === selectedMetricCode;
    });
  const resolvedMetricCode = hasSelectedMetric
    ? selectedMetricCode
    : firstSeriesEntry === undefined
      ? null
      : firstSeriesEntry.metricCode;
  const activeSeries = series.find(function matchActive(entry) {
    return entry.metricCode === resolvedMetricCode;
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-1.5">
        {series.map(function renderChip(entry) {
          const isActive = entry.metricCode === resolvedMetricCode;
          return (
            <Button
              key={entry.metricCode}
              type="button"
              variant={isActive ? 'primary' : 'outline'}
              size="chip"
              onClick={function selectMetric() {
                setSelectedMetricCode(entry.metricCode);
              }}
            >
              {t('metric.' + entry.metricCode)}
            </Button>
          );
        })}
      </div>

      {activeSeries === undefined ? null : <ActiveMetricChart series={activeSeries} />}

      <BeforeAfterComparison entries={comparisonEntries} />
    </div>
  );
}

interface ActiveMetricChartProps {
  series: ProgressMetricSeries;
}

function ActiveMetricChart({ series }: ActiveMetricChartProps) {
  const { t } = useTranslation('progress');
  const unitLabel =
    series.scale.unitCode === null ? t('unit.rating') : t('unit.' + series.scale.unitCode);

  function formatValue(value: number) {
    return formatProgressNumber(value) + ' ' + unitLabel;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs font-bold text-zooindigo">{t('metric.' + series.metricCode)}</h3>
        {series.scale.higherIsBetter === false ? (
          <span className="text-[10px] text-zoomuted italic">{t('section.painNote')}</span>
        ) : null}
      </div>
      <MetricLineChart
        points={series.points.map(toChartPoint)}
        minimumValue={series.scale.minimumValue}
        maximumValue={series.scale.maximumValue}
        accessibleLabel={t('metric.' + series.metricCode)}
        detailLabel={t('chart.feedbackLabel')}
        formatValue={formatValue}
        formatDate={formatIsoDateToDayAndMonth}
      />
      {series.points.length < 2 ? (
        <p className="text-xs text-zoomuted italic mt-1">{t('section.insufficientData')}</p>
      ) : null}
    </div>
  );
}
